<script setup lang="ts">
import { computed, nextTick, ref, type ComponentPublicInstance } from 'vue'
import UiIcon from '@/components/common/UiIcon.vue'
import { normalizeFormKey } from '@/components/form/registry'
import { useDevFormStore } from '@/stores/dev-form.store'
import screeningTests from '../../../screening.json'

type TestOption = {
  value: string
  title: string
  description: string
  type: string
}

type SwitcherOption = {
  value: string | null
  title: string
  description: string
  type: string
}

const devFormStore = useDevFormStore()
devFormStore.init()

const props = withDefaults(defineProps<{
  open?: boolean
}>(), {
  open: false,
})

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const rootEl = ref<HTMLElement | null>(null)
const btnEl = ref<HTMLButtonElement | null>(null)
const open = computed({
  get: () => props.open,
  set: (value: boolean) => emit('update:open', value),
})
const activeIndex = ref(0)
const optionEls = ref<(HTMLButtonElement | null)[]>([])
const menuId = 'dev-test-switcher-menu'
const triggerId = 'dev-test-switcher-trigger'
const panelLabel = 'Switch rendered test'

const testOptions = computed<TestOption[]>(() =>
  screeningTests
    .map((test) => {
      const value = normalizeFormKey(test.key)

      if (!value) {
        return null
      }

      return {
        value,
        title: test.title,
        description: test.description,
        type: test.type,
      }
    })
    .filter((test): test is TestOption => test !== null),
)

const options = computed<SwitcherOption[]>(() => [
  {
    value: null,
    title: 'REST response',
    description: 'Use the form key returned by the current endpoint.',
    type: 'api-driven-form',
  },
  ...testOptions.value,
])

const currentIndex = computed(() => {
  const idx = options.value.findIndex((option) => option.value === devFormStore.selectedKey)
  return idx >= 0 ? idx : 0
})

const currentOption = computed(() => options.value[currentIndex.value] ?? options.value[0])

function setOptionRef(el: Element | ComponentPublicInstance | null, index: number) {
  optionEls.value[index] = el instanceof HTMLButtonElement ? el : null
}

function focusOption(index: number) {
  activeIndex.value = index
  void nextTick(() => {
    optionEls.value[index]?.focus({ preventScroll: true })
  })
}

function openMenu(index?: number) {
  open.value = true
  activeIndex.value = typeof index === 'number' ? index : currentIndex.value

  if (typeof index === 'number') {
    focusOption(index)
  }
}

function close(options: { restoreFocus?: boolean } = {}) {
  open.value = false

  if (options.restoreFocus) {
    void nextTick(() => {
      btnEl.value?.focus({ preventScroll: true })
    })
  }
}

function toggle() {
  if (open.value) {
    close()
    return
  }

  openMenu()
}

function selectOption(option: SwitcherOption) {
  devFormStore.setSelectedKey(option.value)
  close({ restoreFocus: true })
}

function isSelected(option: SwitcherOption) {
  return option.value === devFormStore.selectedKey
}

function moveActive(delta: number) {
  const total = options.value.length
  if (!total) return

  const nextIndex = (activeIndex.value + delta + total) % total
  focusOption(nextIndex)
}

function focusBoundary(position: 'start' | 'end') {
  if (!options.value.length) return
  focusOption(position === 'start' ? 0 : options.value.length - 1)
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openMenu(0)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    openMenu(options.value.length - 1)
  }
}

function onMenuKeydown(event: KeyboardEvent) {
  if (!open.value) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close({ restoreFocus: true })
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveActive(1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(-1)
    return
  }

  if (event.key === 'Home') {
    event.preventDefault()
    focusBoundary('start')
    return
  }

  if (event.key === 'End') {
    event.preventDefault()
    focusBoundary('end')
  }
}

function onFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget

  if (nextTarget instanceof Node && rootEl.value?.contains(nextTarget)) {
    return
  }

  close()
}

</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-350 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        aria-hidden="true"
        class="fixed inset-0 z-30 hidden bg-foreground/25 will-change-[opacity] lg:block"
        @click="close()"
      />
    </Transition>
  </Teleport>

  <div
    ref="rootEl"
    class="hidden items-center lg:flex"
    @focusout="onFocusOut"
  >
    <button
      :id="triggerId"
      ref="btnEl"
      type="button"
      class="utility-trigger utility-trigger--icon"
      :aria-controls="open ? menuId : undefined"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="`${panelLabel}: ${currentOption?.title ?? 'REST response'}`"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <UiIcon
        name="clipboard-clock"
        class="size-6"
      />
      <span class="sr-only">{{ panelLabel }}</span>
    </button>

    <nav
      v-if="open"
      :id="menuId"
      :aria-label="panelLabel"
      :aria-labelledby="triggerId"
      role="menu"
      aria-orientation="vertical"
      tabindex="-1"
      class="absolute inset-x-0 top-full z-50 hidden max-h-[min(38rem,calc(100vh-8rem))] overflow-y-auto rounded-b-2xl bg-surface-elevated text-foreground shadow-2xl lg:block"
      @keydown="onMenuKeydown"
    >
      <div class="mx-auto max-w-screen-2xl px-6 py-6">
        <div class="flex flex-wrap items-center justify-between gap-4 pb-4">
          <div class="flex items-center gap-3">
            <UiIcon
              name="clipboard-clock"
              class="size-5 shrink-0 text-foreground"
            />
            <p class="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              {{ panelLabel }}
            </p>
          </div>

          <p class="text-sm text-muted-foreground">
            {{ options.length - 1 }} tests available
          </p>
        </div>

        <div class="mt-4 grid gap-2 lg:grid-cols-4">
          <button
            v-for="(option, index) in options"
            :key="option.value ?? 'rest-response'"
            :ref="(el) => setOptionRef(el, index)"
            type="button"
            role="menuitemradio"
            class="panel-option items-start"
            :class="[
              { 'bg-accent text-accent-foreground': index === activeIndex || isSelected(option) },
              option.value === null ? 'lg:col-span-4' : '',
            ]"
            :aria-checked="isSelected(option)"
            @mouseenter="activeIndex = index"
            @focus="activeIndex = index"
            @click="selectOption(option)"
          >
            <span class="mt-1 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-deep">
              <UiIcon
                :name="option.value ? 'clipboard-clock' : 'globe'"
                class="size-4"
              />
            </span>
            <span class="min-w-0 flex-1 leading-tight">
              <span class="flex items-center gap-2">
                <span class="block truncate text-sm font-semibold">{{ option.title }}</span>
              </span>
              <span class="mt-1 block text-xs text-muted-foreground">{{ option.description }}</span>
              <!-- <span class="mt-1 block truncate text-[11px] text-muted-foreground/80">{{ option.type }}</span> -->
            </span>
            <!-- <UiIcon
              v-if="isSelected(option)"
              name="check"
              class="mt-2 size-3.5 shrink-0 text-muted-foreground"
            /> -->
          </button>
        </div>
      </div>
    </nav>
  </div>
</template>
